// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE URLA ADDRESS HISTORY — step 1's and the LO form's side of the ONE definition (constants/addressHistory.json,
// a byte-for-byte copy of backend/constants/addressHistory.json). The rules below are the backend's
// constants/addressHistory.js, line for line; backend test/addressHistoryYearsMonths.test.js checks the JSON copy and
// runs both modules on the same cases. See the backend module's header for the stored shape and the old band.
// ─────────────────────────────────────────────────────────────────────────────
import ADDRESS_HISTORY_JSON from './addressHistory.json';

export const DEF = ADDRESS_HISTORY_JSON;

const present = (v) => v !== undefined && v !== null && String(v).trim() !== '';
const ZIP5 = /^\d{5}$/;

/** An integer answer in [0, max], or null (blank / not a whole number / out of range). */
export function intIn(v, max) {
  if (!present(v)) return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0 && n <= max ? n : null;
}
/** { years, months } when BOTH are answered, else null. */
export function durationOf(years, months) {
  const y = intIn(years, DEF.yearsMax);
  const m = intIn(months, DEF.monthsMax);
  return y == null || m == null ? null : { years: y, months: m };
}
/** years × 12 + months, or null when not both answered. */
export function totalMonths(d) { return d ? d.years * 12 + d.months : null; }

export const currentDuration = (fd = {}) => durationOf(fd.address_years, fd.address_months);
export const currentMonths = (fd = {}) => totalMonths(currentDuration(fd));
/** The new question was answered (either control) — the new rules apply from then on. */
export const hasNewAnswer = (fd = {}) => present(fd.address_years) || present(fd.address_months);
/** Answered only with the old band. */
export const isLegacy = (fd = {}) => !hasNewAnswer(fd) && present(fd.address_duration);

export const formersOf = (fd = {}) => (Array.isArray(fd.previous_addresses) ? fd.previous_addresses.filter((a) => a && typeof a === 'object') : []);
export const formerMonths = (a) => totalMonths(durationOf(a && a.years, a && a.months));

/**
 * How many former addresses the history needs: none at 24+ months; otherwise the first, and each next one while the
 * running total is still under 24 months — up to 3. A former address not answered yet ends the count (it is the one
 * owed).
 */
export function requiredFormerCount(curMonths, formers = []) {
  if (curMonths == null || curMonths >= DEF.minMonths) return 0;
  let total = curMonths;
  let k = 0;
  while (total < DEF.minMonths && k < DEF.maxFormer) {
    const m = formerMonths(formers[k]);
    k += 1;
    if (m == null) break;
    total += m;
  }
  return k;
}
/** The former-address blocks to show / keep: at least the first, the ones added, ending where 24 months is reached. */
export function visibleFormerCount(curMonths, formers = []) {
  if (curMonths == null || curMonths >= DEF.minMonths) return 0;
  const n = Math.min(Math.max(1, formers.length), DEF.maxFormer);
  let total = curMonths;
  for (let i = 0; i < n; i += 1) {
    const m = formerMonths(formers[i]);
    if (m != null) total += m;
    if (total >= DEF.minMonths) return i + 1;
  }
  return n;
}
/** Current + the shown former addresses' answered months. */
export function historyMonths(curMonths, formers = []) {
  if (curMonths == null) return null;
  return formers.slice(0, visibleFormerCount(curMonths, formers)).reduce((s, a) => s + (formerMonths(a) || 0), curMonths);
}
/** "Add another former address": shown while the total is under 24 months, the last block answered, fewer than 3. */
export function canAddFormer(curMonths, formers = []) {
  const shown = visibleFormerCount(curMonths, formers);
  if (!shown || shown >= DEF.maxFormer) return false;
  if (formerMonths(formers[shown - 1]) == null) return false;
  return historyMonths(curMonths, formers) < DEF.minMonths;
}
/** What is saved: the shown former addresses only (none at 24+ months). */
export function formersToSave(curMonths, formers = []) {
  return formers.slice(0, visibleFormerCount(curMonths, formers));
}

export const HOUSING_VALUES = DEF.housingTypes.map((h) => h.value);

/**
 * The step-1 errors for the address history (the form's keys: address_years, prev_addr_<part>_<i>, prev_addresses).
 * Always the new rules — the form asks them on every answer of step 1.
 */
export function errorsFor(fd = {}) {
  const e = {};
  const E = DEF.errors;
  const cur = currentMonths(fd);
  if (cur == null) { e.address_years = E.currentDuration; return e; }
  const formers = formersOf(fd);
  const need = requiredFormerCount(cur, formers);
  for (let i = 0; i < need; i += 1) {
    const a = formers[i] || {};
    if (i >= Math.max(1, formers.length)) { e.prev_addresses = E.history; break; }
    if (!present(a.street)) e[`prev_addr_street_${i}`] = E.street;
    if (!present(a.city)) e[`prev_addr_city_${i}`] = E.city;
    if (!present(a.state)) e[`prev_addr_state_${i}`] = E.state;
    if (!ZIP5.test(String(a.zip || '').trim())) e[`prev_addr_zip_${i}`] = E.zip;
    if (formerMonths(a) == null) e[`prev_addr_duration_${i}`] = E.formerDuration;
    if (!HOUSING_VALUES.includes(String(a.housing_status || ''))) e[`prev_addr_housing_${i}`] = E.formerHousing;
  }
  return e;
}

/** The server gate's share (constants/guidedStepGate): the first missing code, or null. New answers only. */
export function firstMissing(fd = {}) {
  const cur = currentMonths(fd);
  if (cur == null) return 'address_years';
  const formers = formersOf(fd);
  const need = requiredFormerCount(cur, formers);
  for (let i = 0; i < need; i += 1) {
    const a = formers[i];
    if (!a) return 'previous_addresses';
    if (!present(a.street) || !present(a.city) || !present(a.state) || !ZIP5.test(String(a.zip || '').trim())
      || formerMonths(a) == null || !HOUSING_VALUES.includes(String(a.housing_status || ''))) return 'previous_address_fields';
  }
  return null;
}

const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
/** "3 years, 4 months" / "30+ years" / "5 months" — or null. */
export function durationText(d) {
  if (!d) return null;
  const y = d.years >= DEF.yearsMax ? `${DEF.yearsMaxLabel} years` : d.years ? plural(d.years, 'year') : '';
  const m = d.months ? plural(d.months, 'month') : '';
  return [y, m].filter(Boolean).join(', ') || '0 months';
}
/** The current address's time, for staff and the 1003: the new answer, else the old band's label, else null. */
export function currentDurationText(fd = {}) {
  const d = currentDuration(fd);
  if (d) return durationText(d);
  return DEF.legacyBands[fd.address_duration] || (present(fd.address_duration) ? String(fd.address_duration) : null);
}
/** A former address's time: its years/months, else its old band's label, else null. */
export function formerDurationText(a = {}) {
  const d = durationOf(a.years, a.months);
  if (d) return durationText(d);
  return DEF.legacyBands[a.duration] || (present(a.duration) ? String(a.duration) : null);
}
export const housingLabel = (v) => (DEF.housingTypes.find((h) => h.value === String(v || '').toLowerCase()) || {}).label || null;
export const housingMismo = (v) => {
  const s = String(v || '').toLowerCase();
  return (DEF.housingTypes.find((h) => h.value === s || (s === 'renting' && h.value === 'rent')) || {}).mismo || null;
};
export const housingFromMismo = (v) => (DEF.housingTypes.find((h) => h.mismo.toLowerCase() === String(v || '').toLowerCase()) || {}).value || null;

/**
 * Every residence, in order: the current one, then each former address with a street (an empty rung is not a
 * residence). months only when the years/months were answered — a band is never turned into a month count.
 */
export function residencesOf(fd = {}) {
  const out = [{ type: 'Current', street: fd.address_street, unit: fd.address_unit, city: fd.address_city, state: fd.address_state,
    zip: fd.address_zip, months: currentMonths(fd), housing: fd.housing_status || null, duration: currentDurationText(fd) }];
  for (const a of formersOf(fd)) {
    if (!present(a.street)) continue;
    out.push({ type: 'Prior', street: a.street, unit: null, city: a.city, state: a.state, zip: a.zip, months: formerMonths(a),
      housing: a.housing_status || null, duration: formerDurationText(a) });
  }
  return out;
}

/** The select options: years 0–30 (30 shown as "30+"), months 0–11. */
export const yearOptions = () => Array.from({ length: DEF.yearsMax + 1 }, (_, i) => ({ value: String(i), label: i === DEF.yearsMax ? DEF.yearsMaxLabel : String(i) }));
export const monthOptions = () => Array.from({ length: DEF.monthsMax + 1 }, (_, i) => ({ value: String(i), label: String(i) }));

/** The parts of each former address on the LO form (constants/submissionVocabulary borrower.prev_address_<n>_<part>). */
export const FORMER_PARTS = ['street', 'city', 'state', 'zip', 'years', 'months', 'housing_status'];
/** The LO form's reveal: former address n is asked while the history before it is under 24 months (vocabulary keys). */
export function formerRevealedFromVocabulary(values = {}, n) {
  const cur = totalMonths(durationOf(values['borrower.address_years'], values['borrower.address_months']));
  const formers = [1, 2, 3].map((k) => ({ years: values[`borrower.prev_address_${k}_years`], months: values[`borrower.prev_address_${k}_months`] }));
  return requiredFormerCount(cur, formers) >= n;
}
